import { Fragment, useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, ArrowDown, ArrowUpDown, Video, FileText, Mail, Users, Paperclip, Plus, Upload, Loader2, Trash2, CheckSquare, Phone, PhoneIncoming, PhoneOutgoing, PlayCircle } from "lucide-react";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import { Button } from "@/components/ui/button";
import ConfirmDeleteDialog from "@/components/shared/ConfirmDeleteDialog";
import ShareRecordDialog from "@/components/shared/ShareRecordDialog";
import { useRecordSharingEnabled } from "@/lib/hooks/use-record-sharing";
import UserAvatar from "@/components/shared/Avatar";
import { cn } from "@/lib/utils";
import { currentUser, workspaceMembers } from "@/data/dummy";
import { format, parseISO, isValid } from "date-fns";
import { displayDate } from "@/lib/i18n/display";
import MailBodyRenderer from "@/components/shared/MailBodyRenderer";
import { formatCallDuration, type CallRow } from "@/lib/utils/call-rows";

/** Format an ISO date string or pre-formatted string into a readable date */
function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  try {
    const d = parseISO(dateStr);
    if (isValid(d)) return displayDate(d);
  } catch { /* ignore parse errors */ }
  return dateStr; // Already formatted or unparsable — return as-is
}

function TabEmptyState({ icon: Icon, title, description, action }: { icon: React.ElementType; title: string; description: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">{title}</p>
      <p className="text-xs text-muted-foreground max-w-[240px]">{description}</p>
      {action && (
        <Button size="sm" className="mt-4 gap-1.5" onClick={action.onClick}>
          <Plus className="h-3.5 w-3.5" />
          {action.label}
        </Button>
      )}
    </div>
  );
}

const internalNames = new Set([
  currentUser.name,
  currentUser.fullName,
  ...workspaceMembers.map(m => m.name),
]);

function sortAttendeesExternalFirst(attendees: string[]): string[] {
  const external = attendees.filter(a => !internalNames.has(a));
  const internal = attendees.filter(a => internalNames.has(a));
  return [...external, ...internal];
}

type SortDir = "asc" | "desc" | null;

interface SortState {
  key: string;
  dir: SortDir;
}

/**
 * Toggle sort state for a column header click. Two-state: clicking a new
 * column sorts ascending; clicking the active column flips asc ↔ desc.
 * (There is intentionally no "unsorted" third state — see issue #1011.)
 */
function nextSort(prev: SortState, key: string): SortState {
  if (prev.key !== key) return { key, dir: "asc" };
  return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active || !dir) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />;
  return dir === "asc"
    ? <ArrowUp className="h-3 w-3 text-foreground" />
    : <ArrowDown className="h-3 w-3 text-foreground" />;
}

function parseDate(d: string): number {
  const t = Date.parse(d);
  return isNaN(t) ? 0 : t;
}

// ----- Meetings Table -----

interface Meeting {
  id: string;
  title: string;
  date: string;
  duration: string;
  attendees: string[];
  // Backend fields (optional, used in PersonDetailSheet)
  startTime?: string;
  displayName?: string;
  name?: string;
  contacts?: Array<{ profileImageUrl?: string | null; properties?: { firstName?: string; lastName?: string; profileImageUrl?: string | null }; primaryEmail?: string }>;
  accounts?: Array<{ properties?: { name?: string }; domain?: string }>;
}

// Inline attendee overflow dropdown — avoids Radix Portal inside Sheet
function AttendeeOverflow({
  attendees,
  profileMap,
  onAttendeeClick,
}: {
  attendees: string[];
  profileMap: Record<string, string | undefined>;
  onAttendeeClick?: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="shrink-0 text-[10px] font-medium bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none hover:bg-accent hover:text-accent-foreground transition"
      >
        +{attendees.length}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-48 rounded-md border border-border bg-popover p-1 shadow-md">
          {attendees.map(name => (
            <button
              key={name}
              onClick={() => { onAttendeeClick?.(name); setOpen(false); }}
              className={cn(
                "flex items-center gap-2 w-full rounded-md px-2.5 py-2 text-sm",
                onAttendeeClick && "hover:bg-muted/50 cursor-pointer"
              )}
            >
              <UserAvatar name={name} size="xs" src={profileMap[name]} />
              <span className={onAttendeeClick ? "text-accent hover:underline" : ""}>{name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MeetingsTable({
  meetings,
  onRowClick,
  onAttendeeClick,
}: {
  meetings: Meeting[];
  onRowClick: (m: Meeting) => void;
  onAttendeeClick?: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "date", dir: "desc" });

  const toggle = (key: string) => {
    setSort(prev => nextSort(prev, key));
  };

  const sorted = useMemo(() => {
    if (!sort.dir) return meetings;
    return [...meetings].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "title") { av = a.title.toLowerCase(); bv = b.title.toLowerCase(); }
      else if (sort.key === "date") { av = parseDate(a.date); bv = parseDate(b.date); }
      else if (sort.key === "attendees") { av = a.attendees.join(", ").toLowerCase(); bv = b.attendees.join(", ").toLowerCase(); }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [meetings, sort]);

  const cols = [
    { key: "title", label: t("grid.drillDown.title"), className: "w-[45%]" },
    { key: "date", label: t("grid.drillDown.date"), className: "w-[25%]" },
    { key: "attendees", label: t("grid.drillDown.attendees"), className: "w-[30%]" },
  ];

  if (meetings.length === 0) {
    return <TabEmptyState icon={Video} title={t("grid.drillDown.noMeetings")} description={t("grid.drillDown.noMeetingsDescription")} />;
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2 px-1">{t("grid.drillDown.showingMeetings")}</p>
      <div className="rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => toggle(c.key)}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(m => (
            <tr
              key={m.id}
              onClick={() => onRowClick(m)}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition cursor-pointer"
            >
              <td className="px-3 py-2.5 font-medium truncate max-w-0">{m.title}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDate(m.date)}</td>
              <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                {(() => {
                  const sorted = sortAttendeesExternalFirst(m.attendees);
                  const profileMap = Object.fromEntries(
                    (m.contacts || []).map(c => {
                      const name = [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail || '';
                      return [name, (c.profileImageUrl ?? c.properties?.profileImageUrl) ?? undefined];
                    })
                  );
                  return sorted.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <UserAvatar name={sorted[0]} size="xs" src={profileMap[sorted[0]]} />
                      {onAttendeeClick ? (
                        <button onClick={() => onAttendeeClick(sorted[0])} className="text-sm truncate max-w-[100px] text-accent hover:underline cursor-pointer">{sorted[0].split(" ")[0]}</button>
                      ) : (
                        <span className="text-sm truncate max-w-[100px]">{sorted[0].split(" ")[0]}</span>
                      )}
                      {sorted.length > 1 && (
                        <AttendeeOverflow
                          attendees={sorted.slice(1)}
                          profileMap={profileMap}
                          onAttendeeClick={onAttendeeClick}
                        />
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  );
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ----- Notes Table -----

export interface NoteRow {
  id: string;
  name: string;
  date: string;
  type: "ai" | "manual";
  content?: string;
}

export function NotesTable({
  notes,
  onRowClick,
  onAdd,
  onDelete,
}: {
  notes: NoteRow[];
  onRowClick: (n: NoteRow) => void;
  onAdd?: () => void;
  onDelete?: (n: NoteRow) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "date", dir: "desc" });
  const [locallyDeleted, setLocallyDeleted] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<NoteRow | null>(null);

  const toggle = (key: string) => {
    setSort(prev => nextSort(prev, key));
  };

  const visible = useMemo(
    () => notes.filter(n => !locallyDeleted.has(n.id)),
    [notes, locallyDeleted],
  );

  const sorted = useMemo(() => {
    if (!sort.dir) return visible;
    return [...visible].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "name") { av = a.name.toLowerCase(); bv = b.name.toLowerCase(); }
      else if (sort.key === "date") { av = parseDate(a.date); bv = parseDate(b.date); }
      else if (sort.key === "type") { av = a.type; bv = b.type; }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [visible, sort]);

  const cols = [
    { key: "name", label: t("grid.drillDown.title"), className: "w-[50%]" },
    { key: "type", label: t("grid.drillDown.type"), className: "w-[20%]" },
    { key: "date", label: t("grid.drillDown.date"), className: "w-[30%]" },
  ];

  if (visible.length === 0) {
    return <TabEmptyState icon={FileText} title={t("grid.drillDown.noNotes")} description={t("grid.drillDown.noNotesDescription")} action={onAdd ? { label: t("grid.drillDown.addNote"), onClick: onAdd } : undefined} />;
  }

  return (
    <>
      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t("grid.drillDown.deleteNote")}
        description={pendingDelete ? t("grid.drillDown.deleteBody", { name: pendingDelete.name }) : ""}
        confirmLabel={t("common.delete")}
        onConfirm={() => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          // Optimistically hide the row; un-hide if the delete rejects so the
          // UI doesn't show it gone while it still exists on the server.
          setLocallyDeleted(prev => { const n = new Set(prev); n.add(target.id); return n; });
          Promise.resolve(onDelete?.(target)).catch(() => {
            setLocallyDeleted(prev => { const n = new Set(prev); n.delete(target.id); return n; });
          });
          setPendingDelete(null);
        }}
      />
      <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => toggle(c.key)}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(n => (
            <tr
              key={n.id}
              onClick={() => onRowClick(n)}
              className="group border-b border-border last:border-0 hover:bg-muted/30 transition cursor-pointer"
            >
              <td className="px-3 py-2.5 font-medium truncate max-w-0">{n.name}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                <span className={cn(
                  "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
                  n.type === "ai" ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"
                )}>
                  {n.type === "ai" ? t("grid.drillDown.noteTypeAi") : t("grid.drillDown.noteTypeManual")}
                </span>
              </td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                <div className="flex items-center justify-between gap-2">
                  <span>{formatDate(n.date)}</span>
                  <button
                    type="button"
                    aria-label={`Delete ${n.name}`}
                    onClick={(e) => { e.stopPropagation(); setPendingDelete(n); }}
                    className="opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-destructive p-1 rounded"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

// ----- Emails Table -----

// ----- Tasks Table -----

export interface TaskRow {
  id: string;
  title: string;
  dueDate: string;
  assignee: string;
  completed?: boolean;
}

export function TasksTable({
  tasks,
  onRowClick,
  onAdd,
  onDelete,
}: {
  tasks: TaskRow[];
  onRowClick?: (t: TaskRow) => void;
  onAdd?: () => void;
  onDelete?: (t: TaskRow) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "dueDate", dir: "asc" });
  const [locallyDeleted, setLocallyDeleted] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<TaskRow | null>(null);

  const toggle = (key: string) => {
    setSort(prev => nextSort(prev, key));
  };

  const visible = useMemo(
    () => tasks.filter(t => !locallyDeleted.has(t.id)),
    [tasks, locallyDeleted],
  );

  const sorted = useMemo(() => {
    if (!sort.dir) return visible;
    return [...visible].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "title") { av = a.title.toLowerCase(); bv = b.title.toLowerCase(); }
      else if (sort.key === "dueDate") { av = parseDate(a.dueDate); bv = parseDate(b.dueDate); }
      else if (sort.key === "assignee") { av = a.assignee.toLowerCase(); bv = b.assignee.toLowerCase(); }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [visible, sort]);

  const cols = [
    { key: "title", label: t("grid.drillDown.title"), className: "w-[55%]" },
    { key: "dueDate", label: t("grid.drillDown.due"), className: "w-[20%]" },
    { key: "assignee", label: t("grid.drillDown.assignedTo"), className: "w-[25%]" },
  ];

  if (visible.length === 0) {
    return <TabEmptyState icon={CheckSquare} title={t("grid.drillDown.noTasks")} description={t("grid.drillDown.noTasksDescription")} action={onAdd ? { label: t("grid.drillDown.addTask"), onClick: onAdd } : undefined} />;
  }

  return (
    <>
      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t("grid.drillDown.deleteTask")}
        description={pendingDelete ? t("grid.drillDown.deleteBody", { name: pendingDelete.title }) : ""}
        confirmLabel={t("common.delete")}
        onConfirm={() => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          // Optimistically hide the row; un-hide if the delete rejects so the
          // UI doesn't show it gone while it still exists on the server.
          setLocallyDeleted(prev => { const n = new Set(prev); n.add(target.id); return n; });
          Promise.resolve(onDelete?.(target)).catch(() => {
            setLocallyDeleted(prev => { const n = new Set(prev); n.delete(target.id); return n; });
          });
          setPendingDelete(null);
        }}
      />
      <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => toggle(c.key)}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(t => (
            <tr
              key={t.id}
              onClick={() => onRowClick?.(t)}
              className={cn(
                "group border-b border-border last:border-0 hover:bg-muted/30 transition",
                onRowClick && "cursor-pointer",
              )}
            >
              <td className={cn("px-3 py-2.5 font-medium truncate max-w-0", t.completed && "line-through text-muted-foreground")}>{t.title}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDate(t.dueDate)}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <UserAvatar name={t.assignee} size="xs" />
                    <span className="truncate">{t.assignee}</span>
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete ${t.title}`}
                    onClick={(e) => { e.stopPropagation(); setPendingDelete(t); }}
                    className="opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-destructive p-1 rounded shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

interface Email {
  id: string;
  subject: string;
  date: string;
  from: string;
  direction: "inbound" | "outbound";
  // Backend fields (optional, used in PersonDetailSheet)
  displayName?: string;
  name?: string;
  startTime?: string;
  contacts?: Array<{ profileImageUrl?: string | null; properties?: { firstName?: string; lastName?: string; profileImageUrl?: string | null }; primaryEmail?: string }>;
  accounts?: Array<{ properties?: { name?: string }; domain?: string }>;
}

export function EmailsTable({
  emails,
  onRowClick,
  onSenderClick,
}: {
  emails: Email[];
  onRowClick: (e: Email) => void;
  onSenderClick?: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "date", dir: "desc" });

  const toggle = (key: string) => {
    setSort(prev => nextSort(prev, key));
  };

  const sorted = useMemo(() => {
    if (!sort.dir) return emails;
    return [...emails].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "subject") { av = a.subject.toLowerCase(); bv = b.subject.toLowerCase(); }
      else if (sort.key === "from") { av = a.from.toLowerCase(); bv = b.from.toLowerCase(); }
      else if (sort.key === "date") { av = parseDate(a.date); bv = parseDate(b.date); }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [emails, sort]);

  const cols = [
    { key: "subject", label: t("grid.drillDown.subject"), className: "w-[50%]" },
    { key: "from", label: t("grid.drillDown.sender"), className: "w-[25%]" },
    { key: "date", label: t("grid.drillDown.date"), className: "w-[25%]" },
  ];

  if (emails.length === 0) {
    return <TabEmptyState icon={Mail} title={t("grid.drillDown.noEmails")} description={t("grid.drillDown.noEmailsDescription")} />;
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2 px-1">{t("grid.drillDown.showingEmails")}</p>
      <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => toggle(c.key)}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(e => (
            <tr
              key={e.id}
              onClick={() => onRowClick(e)}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition cursor-pointer"
            >
              <td className="px-3 py-2.5 font-medium truncate max-w-0">{e.subject}</td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  {(() => {
                    const senderContact = (e.contacts || []).find(c => {
                      const name = [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail || '';
                      return name === e.from;
                    });
                    const senderImg = (senderContact?.profileImageUrl ?? senderContact?.properties?.profileImageUrl) ?? undefined;
                    return <UserAvatar name={e.from} size="xs" src={senderImg} />;
                  })()}
                  {onSenderClick && e.direction === "inbound" ? (
                    <button
                      onClick={(ev) => { ev.stopPropagation(); onSenderClick(e.from); }}
                      className="text-sm truncate max-w-[100px] text-accent hover:underline cursor-pointer"
                    >
                      {e.from.split(" ")[0]}
                    </button>
                  ) : (
                    <span className="text-sm truncate max-w-[100px]">{e.from.split(" ")[0]}</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDate(e.date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ----- Calls Table -----

export function CallsTable({ calls }: { calls: CallRow[] }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "date", dir: "desc" });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const callTitle = (c: CallRow) =>
    c.title ||
    (c.direction === "INBOUND" ? t("grid.drillDown.inboundCall")
      : c.direction === "OUTBOUND" ? t("grid.drillDown.outboundCall")
      : t("grid.drillDown.call"));

  const sorted = useMemo(() => {
    if (!sort.dir) return calls;
    return [...calls].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "call") { av = callTitle(a).toLowerCase(); bv = callTitle(b).toLowerCase(); }
      else if (sort.key === "outcome") { av = (a.outcome || "").toLowerCase(); bv = (b.outcome || "").toLowerCase(); }
      else if (sort.key === "duration") { av = a.durationMs ?? 0; bv = b.durationMs ?? 0; }
      else if (sort.key === "date") { av = parseDate(a.startTime); bv = parseDate(b.startTime); }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, sort]);

  const cols = [
    { key: "call", label: t("grid.drillDown.call"), className: "w-[40%]" },
    { key: "outcome", label: t("grid.drillDown.outcome"), className: "w-[22%]" },
    { key: "duration", label: t("grid.drillDown.duration"), className: "w-[14%]" },
    { key: "date", label: t("grid.drillDown.date"), className: "w-[24%]" },
  ];

  if (calls.length === 0) {
    return <TabEmptyState icon={Phone} title={t("grid.drillDown.noCalls")} description={t("grid.drillDown.noCallsDescription")} />;
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2 px-1">{t("grid.drillDown.showingCalls")}</p>
      <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => setSort(prev => nextSort(prev, c.key))}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(c => {
            const expanded = expandedId === c.id;
            const DirIcon = c.direction === "INBOUND" ? PhoneIncoming : c.direction === "OUTBOUND" ? PhoneOutgoing : Phone;
            return (
              <Fragment key={c.id}>
                <tr
                  onClick={() => setExpandedId(expanded ? null : c.id)}
                  aria-expanded={expanded}
                  className={cn("border-b border-border last:border-0 hover:bg-muted/30 transition cursor-pointer", expanded && "bg-muted/20")}
                >
                  <td className="px-3 py-2.5 font-medium truncate max-w-0">
                    <span className="inline-flex items-center gap-2 max-w-full">
                      <DirIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{callTitle(c)}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground truncate max-w-0">{c.outcome || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap tabular-nums">{formatCallDuration(c.durationMs) || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDate(c.startTime)}</td>
                </tr>
                {expanded && (
                  <tr className="border-b border-border last:border-0 bg-muted/10">
                    <td colSpan={cols.length} className="px-4 py-3 space-y-2">
                      {(c.fromNumber || c.toNumber) && (
                        <p className="text-xs text-muted-foreground tabular-nums">
                          {[c.fromNumber, c.toNumber].filter(Boolean).join(" → ")}
                        </p>
                      )}
                      {c.notesHtml
                        ? <MailBodyRenderer body="" bodyHtml={c.notesHtml} />
                        : <p className="text-xs text-muted-foreground">{t("grid.drillDown.noCallNotes")}</p>}
                      {c.recordingUrl && (
                        <a
                          href={c.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(ev) => ev.stopPropagation()}
                          className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                        >
                          <PlayCircle className="h-3.5 w-3.5" />
                          {t("grid.drillDown.listenToRecording")}
                        </a>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ----- People Table -----

interface PersonRow {
  id: string;
  name: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
}

export function PeopleTable({
  people,
  onRowClick,
  showCompany = false,
  emptyMessage,
}: {
  people: PersonRow[];
  onRowClick: (p: PersonRow) => void;
  showCompany?: boolean;
  emptyMessage?: string;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "name", dir: "asc" });

  const toggle = (key: string) => {
    setSort(prev => nextSort(prev, key));
  };

  const sorted = useMemo(() => {
    if (!sort.dir) return people;
    return [...people].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "name") { av = a.name.toLowerCase(); bv = b.name.toLowerCase(); }
      else if (sort.key === "title") { av = (a.title || "").toLowerCase(); bv = (b.title || "").toLowerCase(); }
      else if (sort.key === "company") { av = (a.company || "").toLowerCase(); bv = (b.company || "").toLowerCase(); }
      else if (sort.key === "email") { av = (a.email || "").toLowerCase(); bv = (b.email || "").toLowerCase(); }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [people, sort]);

  const cols = showCompany
    ? [
        { key: "name", label: t("grid.drillDown.name"), className: "w-[30%]" },
        { key: "title", label: t("grid.drillDown.title"), className: "w-[30%]" },
        { key: "company", label: t("grid.drillDown.company"), className: "w-[20%]" },
        { key: "email", label: t("grid.drillDown.email"), className: "w-[20%]" },
      ]
    : [
        { key: "name", label: t("grid.drillDown.name"), className: "w-[35%]" },
        { key: "title", label: t("grid.drillDown.title"), className: "w-[35%]" },
        { key: "email", label: t("grid.drillDown.email"), className: "w-[30%]" },
      ];

  if (people.length === 0) {
    return <TabEmptyState icon={Users} title={t("grid.drillDown.noPeople")} description={emptyMessage ?? t("grid.drillDown.noPeopleFound")} />;
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => toggle(c.key)}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(p => (
            <tr
              key={p.id}
              onClick={() => onRowClick(p)}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition cursor-pointer"
            >
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <UserAvatar name={p.name} size="xs" />
                  <span className="font-medium truncate">{p.name}</span>
                </div>
              </td>
              <td className="px-3 py-2.5 text-muted-foreground truncate max-w-0">{p.title || "—"}</td>
              {showCompany && <td className="px-3 py-2.5 text-muted-foreground truncate max-w-0">{p.company || "—"}</td>}
              <td className="px-3 py-2.5 text-muted-foreground truncate max-w-0">{p.email || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Also export TabEmptyState for use in detail sheets
export { TabEmptyState };

// ----- Files Table -----

interface FileRow {
  id: string;
  name: string;
  size: string;
  date: string;
  [key: string]: any;
}

function getFileType(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  const typeMap: Record<string, string> = {
    pdf: "PDF", docx: "Word", doc: "Word", xlsx: "Excel", xls: "Excel",
    pptx: "PowerPoint", ppt: "PowerPoint", csv: "CSV", txt: "Text",
    png: "Image", jpg: "Image", jpeg: "Image", gif: "Image", svg: "SVG",
    zip: "Archive", mp4: "Video", mp3: "Audio",
  };
  return typeMap[ext] || ext.toUpperCase();
}

export function FilesTable({
  files,
  onRowClick,
  onFileUpload,
  uploading,
  uploadProgress,
  onDelete,
}: {
  files: FileRow[];
  onRowClick: (f: FileRow) => void;
  onFileUpload?: (file: File) => void;
  uploading?: boolean;
  uploadProgress?: number;
  onDelete?: (file: FileRow) => void;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: "date", dir: "desc" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showUpload = useFeatureFlag(FEATURE_FLAGS.MANUAL_FILE_UPLOAD);
  const [locallyDeleted, setLocallyDeleted] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<FileRow | null>(null);
  // Per-record group sharing — one dialog instance for the table (grants load
  // when it opens), covering files in every detail sheet with no per-sheet wiring.
  const { enabled: recordSharingEnabled } = useRecordSharingEnabled();
  const [shareTarget, setShareTarget] = useState<FileRow | null>(null);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onFileUpload) {
      onFileUpload(file);
    }
    // Reset so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [onFileUpload]);

  const toggle = (key: string) => {
    setSort(prev => nextSort(prev, key));
  };

  const visibleFiles = useMemo(
    () => files.filter(f => !locallyDeleted.has(f.id)),
    [files, locallyDeleted],
  );

  const sorted = useMemo(() => {
    if (!sort.dir) return visibleFiles;
    return [...visibleFiles].sort((a, b) => {
      let av: any, bv: any;
      if (sort.key === "name") { av = a.name.toLowerCase(); bv = b.name.toLowerCase(); }
      else if (sort.key === "type") { av = getFileType(a.name); bv = getFileType(b.name); }
      else if (sort.key === "size") { av = a.size; bv = b.size; }
      else if (sort.key === "date") { av = parseDate(a.date); bv = parseDate(b.date); }
      else return 0;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [visibleFiles, sort]);

  const cols = [
    { key: "name", label: t("grid.drillDown.fileName"), className: "w-[40%]" },
    { key: "type", label: t("grid.drillDown.type"), className: "w-[15%]" },
    { key: "size", label: t("grid.drillDown.size"), className: "w-[20%]" },
    { key: "date", label: t("grid.drillDown.date"), className: "w-[25%]" },
  ];

  {/* Hidden file input for native file picker */}
  const hiddenInput = showUpload ? (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      onChange={handleFileChange}
    />
  ) : null;

  if (visibleFiles.length === 0) {
    return (
      <>
        {hiddenInput}
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <Paperclip className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">{t("grid.drillDown.noFiles")}</p>
          <p className="text-xs text-muted-foreground max-w-[240px] mb-4">{t("grid.drillDown.noFilesDescription")}</p>
          {showUpload && (
            <Button variant="outline" size="sm" onClick={handleUploadClick} disabled={uploading} className="gap-1.5">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? t("grid.drillDown.uploading", { percent: uploadProgress ?? 0 }) : t("grid.drillDown.uploadFile")}
            </Button>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {hiddenInput}
      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t("grid.drillDown.deleteFile")}
        description={pendingDelete ? t("grid.drillDown.deleteBody", { name: pendingDelete.name }) : ""}
        confirmLabel={t("common.delete")}
        onConfirm={() => {
          if (!pendingDelete) return;
          setLocallyDeleted(prev => {
            const next = new Set(prev);
            next.add(pendingDelete.id);
            return next;
          });
          onDelete?.(pendingDelete);
          setPendingDelete(null);
        }}
      />
      {showUpload && (
        <div className="flex justify-end mb-2">
          <Button variant="outline" size="sm" onClick={handleUploadClick} disabled={uploading} className="gap-1.5">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? t("grid.drillDown.uploading", { percent: uploadProgress ?? 0 }) : t("grid.drillDown.uploadFile")}
          </Button>
        </div>
      )}
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {cols.map(c => (
              <th
                key={c.key}
                className={cn("px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition", c.className)}
                onClick={() => toggle(c.key)}
              >
                <span className={cn("inline-flex items-center gap-1", sort.key === c.key && sort.dir && "text-foreground font-semibold")}>
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.key === c.key ? sort.dir : null} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(f => (
            <tr
              key={f.id}
              onClick={() => onRowClick(f)}
              className="group border-b border-border last:border-0 hover:bg-muted/30 transition cursor-pointer"
            >
              <td className="px-3 py-2.5 font-medium truncate max-w-0">{f.name}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{getFileType(f.name)}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{f.size}</td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                <div className="flex items-center justify-between gap-2">
                  <span>{formatDate(f.date)}</span>
                  <span className="flex items-center">
                    {recordSharingEnabled && (
                      <button
                        type="button"
                        aria-label={`Share ${f.name}`}
                        onClick={(e) => { e.stopPropagation(); setShareTarget(f); }}
                        className="opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-foreground p-1 rounded"
                      >
                        <Users className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label={`Delete ${f.name}`}
                      onClick={(e) => { e.stopPropagation(); setPendingDelete(f); }}
                      className="opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-destructive p-1 rounded"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {shareTarget && (
        <ShareRecordDialog
          open={!!shareTarget}
          onOpenChange={(open) => !open && setShareTarget(null)}
          recordId={shareTarget.id}
          recordName={shareTarget.name}
          recordType="file"
        />
      )}
    </>
  );
}
